
import DetailsTab from "./DetailTab";
import AvailabilityTab from "./AvailabilityTab";
import ImagesTab from "./ImagesTab";
import ItemCustomizationTab from "./ItemCustomizationTab";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    Button,
    Card,
    Divider,
    Form,
    Input,
    List,
    Select,
    Tabs,
} from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    CopyOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import axios from "utils/axios";
import configFile from "config.json";
import { fetchMajorGroups } from "utils/etc";
import { NotificationManager } from "react-notifications";

const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;

const mainColors = {
    bg: "#f5f7fb",
    surface: "#ffffff",
    surfaceAlt: "#f0f4ff",
    textPrimary: "#0f172a",
    textSecondary: "#64748b",
    border: "rgba(15, 23, 42, 0.08)",
    accent: "#2563eb",
    accentSoft: "#e0e7ff",
};

const resolveTagLabel = (tag) => {
    if (typeof tag === "string" || typeof tag === "number") {
        return String(tag).trim();
    }
    if (!tag || typeof tag !== "object") {
        return "";
    }
    const candidate = tag.slug ?? tag.name ?? tag.label ?? tag.title ?? tag.id ?? tag.value;
    if (typeof candidate === "string" || typeof candidate === "number") {
        return String(candidate).trim();
    }
    return "";
};

const resolveTagColor = (tag) => {
    if (!tag || typeof tag !== "object") {
        return "";
    }
    const candidate = tag.color ?? tag.hexColor ?? tag.tagColor;
    return typeof candidate === "string" ? candidate.trim() : "";
};

const normalizeHexColor = (value) => {
    if (typeof value !== "string") {
        return "";
    }
    const trimmed = value.trim();
    const shortHexMatch = /^#([0-9a-fA-F]{3})$/.exec(trimmed);
    if (shortHexMatch) {
        const [r, g, b] = shortHexMatch[1].split("");
        return `#${r}${r}${g}${g}${b}${b}`;
    }
    return /^#([0-9a-fA-F]{6})$/.test(trimmed) ? trimmed : "";
};

const hexToRgba = (hex, alpha) => {
    const normalized = normalizeHexColor(hex);
    if (!normalized) {
        return "";
    }
    const r = Number.parseInt(normalized.slice(1, 3), 16);
    const g = Number.parseInt(normalized.slice(3, 5), 16);
    const b = Number.parseInt(normalized.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getTagBadges = (tags) => {
    if (!Array.isArray(tags)) {
        return [];
    }
    const badgeByLabel = new Map();
    tags.forEach((tag) => {
        const label = resolveTagLabel(tag);
        if (!label) {
            return;
        }
        const key = label.toLowerCase();
        const color = resolveTagColor(tag);
        const previous = badgeByLabel.get(key);
        if (!previous) {
            badgeByLabel.set(key, { label, color });
            return;
        }
        if (!previous.color && color) {
            badgeByLabel.set(key, { ...previous, color });
        }
    });
    return Array.from(badgeByLabel.values());
};

const getTagBadgeStyle = (color) => {
    const normalized = normalizeHexColor(color);
    if (!normalized) {
        return undefined;
    }
    return {
        borderColor: hexToRgba(normalized, 0.45),
        background: hexToRgba(normalized, 0.16),
        color: normalized,
    };
};

const resolveTagId = (tag) => {
    if (typeof tag === "string" || typeof tag === "number") {
        return String(tag).trim();
    }
    if (!tag || typeof tag !== "object") {
        return "";
    }
    const candidate = tag.id ?? tag.value ?? tag.tagId;
    if (typeof candidate === "string" || typeof candidate === "number") {
        return String(candidate).trim();
    }
    return "";
};

const Products = () => {
    const [products, setProducts] = useState([]);
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [selectedId, setSelectedId] = useState("");
    const [majorGroups, setMajorGroups] = useState([]);
    const [selectedMajorGroup, setSelectedMajorGroup] = useState(null);
    const [form] = Form.useForm();
    const [statusValue, setStatusValue] = useState(true);
    const [isVisibleValue, setIsVisibleValue] = useState(true);
    const [imageSlots, setImageSlots] = useState(null);
    const [availableImages, setAvailableImages] = useState([]);
    const [activeTab, setActiveTab] = useState("details");
    const [modifiers, setModifiers] = useState({ sizes: [], bases: [] });
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const selectedProduct = useMemo(
        () => products.find((item) => item.id === selectedId) || null,
        [products, selectedId]
    );

    const majorGroupOptions = useMemo(
        () =>
            majorGroups.map((group) => ({
                value: group.id,
                label: group.name,
            })),
        [majorGroups]
    );

    const resetSelectedProduct = useCallback(() => {
        form.resetFields();
        setStatusValue(true);
        setIsVisibleValue(true);
        setImageSlots(null);
    }, [form]);

    const fetchImages = useCallback(async () => {
        try {
            const { data: result } = await axios.post(`${API_BASE}/image/list`);
            setAvailableImages(result?.data || []);
        } catch (err) {
            console.log(err);
        }
    }, []);

    const fetchProducts = useCallback(async (majorGroupId) => {
        try {
            if (!majorGroupId) {
                setProducts([]);
                return;
            }
            const { data: result } = await axios.post(
                `${API_BASE}/menu/getMenus`,
                { majorGroupId, withDefinitions: true }
            );
            const [firstKey] = Object.keys(result?.data || {});
            setProducts(result?.data?.[firstKey] || []);
            setSelectedId("");
        } catch (err) {
            console.log(err);
        }
    }, []);

    const fetchTags = useCallback(async () => {
        try {
            const { data: result } = await axios.post(`${configFile.PROD_SERVER_URL}/tag/getAllTags`);
            const rows = Array.isArray(result?.data)
                ? result.data
                : Array.isArray(result)
                    ? result
                    : [];
            setTags(rows);
        } catch (err) {
            console.log(err);
        }
    }, []);

    const tagOptions = useMemo(() => {
        const optionsById = new Map();
        tags.forEach((tag) => {
            const id = resolveTagId(tag);
            if (!id || optionsById.has(id)) {
                return;
            }
            optionsById.set(id, {
                value: id,
                label: resolveTagLabel(tag) || id,
            });
        });
        return Array.from(optionsById.values()).sort((a, b) =>
            a.label.localeCompare(b.label)
        );
    }, [tags]);

    const filteredProducts = useMemo(() => {
        const value = searchValue.trim().toLowerCase();
        const selectedTagSet = new Set(
            selectedTags
                .map((tagId) => String(tagId).trim())
                .filter(Boolean)
        );
        const labelById = new Map(
            majorGroups.map((group) => [group.id, group.name])
        );
        return products.filter((item) => {
            const matchesSearch =
                !value ||
                [
                    item.name,
                    labelById.get(item.majorGroupId),
                    item.majorGroupId,
                ]
                    .filter((field) => field !== null && typeof field !== "undefined")
                    .some((field) =>
                        String(field).toLowerCase().includes(value)
                    );
            if (!matchesSearch) {
                return false;
            }
            if (selectedTagSet.size === 0) {
                return true;
            }
            const itemTagIds = new Set(
                (Array.isArray(item.tags) ? item.tags : [])
                    .map((tag) => resolveTagId(tag))
                    .filter(Boolean)
            );
            return Array.from(selectedTagSet).some((tagId) =>
                itemTagIds.has(tagId)
            );
        });
    }, [majorGroups, products, searchValue, selectedTags]);

    const handleCreateProduct = useCallback(() => {
        if (!selectedMajorGroup) {
            return;
        }
        setActiveTab("details");
        const draftId = `draft_${Date.now()}`;
        const draftItem = {
            id: draftId,
            isDraft: true,
            name: "Шинэ бүтээгдэхүүн",
            majorGroupId: selectedMajorGroup,
            defaultPrice: 0,
            price: 0,
            status: "active",
            isActive: true,
            isVisible: true,
            tags: [],
            description: "",
            prepTime: null,
            images: null,
            definitions: [],
            sizes: [],
            bases: [],
            translations: {},
        };
        setProducts((prev) => [draftItem, ...prev]);
        setSelectedId(draftId);
    }, [selectedMajorGroup]);

    const handleDuplicateProduct = useCallback(async () => {
        if (!selectedProduct || selectedProduct?.isDraft || !selectedMajorGroup) {
            return;
        }
        setIsDuplicating(true);
        try {
            await axios.post(`${API_BASE}/manageMenu/duplicateMenu`, {
                menuId: selectedProduct.id,
            });
            await fetchProducts(selectedMajorGroup);
            NotificationManager.success("Амжилттай хууллаа");
        } catch (err) {
            NotificationManager.error(
                err?.response?.data?.errorMessage || "Хуулж чадсангүй",
                "Error",
                5000
            );
        } finally {
            setIsDuplicating(false);
        }
    }, [fetchProducts, selectedMajorGroup, selectedProduct]);

    const handleDeleteProduct = useCallback(async () => {
        if (!selectedProduct || selectedProduct?.isDraft || !selectedMajorGroup) {
            return;
        }
        setIsDeleting(true);
        try {
            await axios.post(`${API_BASE}/manageMenu/deleteMenu`, {
                menuId: selectedProduct.id,
            });
            await fetchProducts(selectedMajorGroup);
            NotificationManager.success("Амжилттай устгалаа");
        } catch (err) {
            NotificationManager.error(
                err?.response?.data?.errorMessage || "Устгаж чадсангүй",
                "Error",
                5000
            );
        } finally {
            setIsDeleting(false);
        }
    }, [fetchProducts, selectedMajorGroup, selectedProduct]);


    const handleImageSelect = useCallback((image) => {
        if (!selectedProduct) {
            return;
        }
        setImageSlots(image);
        setProducts((items) =>
            items.map((item) =>
                item.id === selectedProduct.id
                    ? { ...item, imageUrl: image?.url ?? null }
                    : item
            )
        );
    }, [selectedProduct]);

    const handleDefinitionChange = useCallback((definitionId, patch) => {
        if (!selectedProduct) {
            return;
        }
        setProducts((prev) =>
            prev.map((item) =>
                item.id === selectedProduct.id
                    ? {
                        ...item,
                        definitions: (item.definitions || []).map((row) =>
                            row.id === definitionId ? { ...row, ...patch } : row
                        ),
                    }
                    : item
            )
        );
    }, [selectedProduct]);

    const handleAddDefinition = useCallback(() => {
        if (!selectedProduct) {
            return;
        }
        const draftId = `draft_def_${Date.now()}`;
        const newRow = {
            id: draftId,
            menuId: selectedProduct.isDraft ? null : selectedProduct.id,
            majorGroupId: selectedProduct.majorGroupId,
            type: "",
            masterName: "",
            miMasterObjNum: "",
            sizeId: null,
            baseId: null,
            price: 0,
            pickupPrice: 0,
            ableMenu: true,
            ableCombo: false,
        };
        setProducts((prev) =>
            prev.map((item) =>
                item.id === selectedProduct.id
                    ? {
                        ...item,
                        definitions: [...(item.definitions || []), newRow],
                    }
                    : item
            )
        );
    }, [selectedProduct]);

    const handleDefinitionDelete = useCallback((definitionId) => {
        if (!selectedProduct) {
            return;
        }
        setProducts((prev) =>
            prev.map((item) =>
                item.id === selectedProduct.id
                    ? {
                        ...item,
                        definitions: (item.definitions || []).filter(
                            (row) => row.id !== definitionId
                        ),
                    }
                    : item
            )
        );
    }, [selectedProduct]);

    const handleTagAssigned = useCallback((menuId, tag) => {
        if (
            menuId === null ||
            typeof menuId === "undefined" ||
            !tag
        ) {
            return;
        }
        const incomingTagId = resolveTagId(tag);
        if (!incomingTagId) {
            return;
        }
        setProducts((prev) =>
            prev.map((item) => {
                if (item.id !== menuId) {
                    return item;
                }
                const currentTags = Array.isArray(item.tags) ? item.tags : [];
                const hasTag = currentTags.some(
                    (singleTag) => resolveTagId(singleTag) === incomingTagId
                );
                if (hasTag) {
                    return item;
                }
                return {
                    ...item,
                    tags: [...currentTags, tag],
                };
            })
        );
    }, []);

    const handleTagUnassigned = useCallback((menuId, tagId) => {
        if (
            menuId === null ||
            typeof menuId === "undefined" ||
            tagId === null ||
            typeof tagId === "undefined"
        ) {
            return;
        }
        const removedTagId = String(tagId).trim();
        setProducts((prev) =>
            prev.map((item) => {
                if (item.id !== menuId) {
                    return item;
                }
                return {
                    ...item,
                    tags: (Array.isArray(item.tags) ? item.tags : []).filter(
                        (tag) => resolveTagId(tag) !== removedTagId
                    ),
                };
            })
        );
    }, []);

    const fetchMajorGroupData = useCallback(async () => {
        // TODO: Authorization
        const { data } = await fetchMajorGroups();
        setMajorGroups(data || []);
        setSelectedMajorGroup((prev) =>
            (data || []).some((group) => group.id === prev) ? prev : null
        );
    }, []);

    const fetchSizeOptions = useCallback(async () => {
        try {
            const { data: response } = await axios.post(`${API_BASE}/option/listSizes`);
            setModifiers((prev) => ({
                ...prev,
                sizes: response.data || [],
            }));
        } catch (err) {
            console.log(err);
        }
    }, []);

    const fetchBaseOptions = useCallback(async () => {
        try {
            const { data: response } = await axios.post(`${API_BASE}/option/listBases`);
            setModifiers((prev) => ({
                ...prev,
                bases: response.data || [],
            }));
        } catch (err) {
            console.log(err);
        }
    }, []);

    useEffect(() => {
        fetchMajorGroupData();
        fetchBaseOptions();
        fetchSizeOptions();
        fetchImages();
        fetchTags();
    }, [fetchBaseOptions, fetchMajorGroupData, fetchSizeOptions, fetchImages, fetchTags]);
    useEffect(() => {
        setProducts([]);
        setSelectedId("");
        setSelectedTags([]);
        if (!selectedMajorGroup) {
            return;
        }
        fetchProducts(selectedMajorGroup);
    }, [fetchProducts, selectedMajorGroup]);
    useEffect(() => {
        if (!selectedProduct) {
            resetSelectedProduct();
            return;
        }
        form.setFieldsValue({
            name: selectedProduct.name,
            majorGroupId: selectedProduct.majorGroupId,
            defaultPrice: selectedProduct.defaultPrice,
            price: selectedProduct.price,
            tags: (selectedProduct.tags || [])
                .map((tag) => tag?.id ?? tag)
                .filter((tag) => tag !== null && typeof tag !== "undefined"),
            description: selectedProduct.description,
            prepTime: selectedProduct.prepTime,
            type: selectedProduct.type,
            isActive:
                typeof selectedProduct.isActive === "boolean"
                    ? selectedProduct.isActive
                    : selectedProduct.status === "active"
        });
        const translationRows = Object.entries(
            selectedProduct.translations || {}
        ).map(([lang, value]) => ({
            lang,
            name: value?.name ?? "",
            description: value?.description ?? "",
        }));
        form.setFieldsValue({
            translations: translationRows,
        });
        const translationEntries = Object.entries(
            selectedProduct.translations || {}
        );
        const [firstTranslation, secondTranslation] = translationEntries;
        form.setFieldsValue({
            translationNameLang1: firstTranslation?.[0] ?? null,
            translationNameText1: firstTranslation?.[1]?.name ?? "",
            translationNameLang2: secondTranslation?.[0] ?? null,
            translationNameText2: secondTranslation?.[1]?.name ?? "",
            translationDescLang1: firstTranslation?.[0] ?? null,
            translationDescText1: firstTranslation?.[1]?.description ?? "",
            translationDescLang2: secondTranslation?.[0] ?? null,
            translationDescText2: secondTranslation?.[1]?.description ?? "",
        });
        setStatusValue(selectedProduct.status === "active");
        setIsVisibleValue(selectedProduct.isVisible ?? true);
        setImageSlots(selectedProduct.imageUrl ? { url: selectedProduct.imageUrl } : null);
    }, [form, resetSelectedProduct, selectedProduct]);

    const selectedMajorGroupLabel = useMemo(
        () => majorGroups.find((group) => group.id === selectedMajorGroup)?.name || "",
        [majorGroups, selectedMajorGroup]
    );

    const selectedProductGroupLabel = useMemo(
        () =>
            selectedProduct?.majorGroupLabel ||
            majorGroups.find((group) => group.id === selectedProduct?.majorGroupId)
                ?.name ||
            selectedProduct?.majorGroupId ||
            "",
        [majorGroups, selectedProduct]
    );

    return (
        <PageWrap>
            <HeaderRow>
                <TitleBlock>
                    <h1>Бүтээгдэхүүн өөрчлөх</h1>
                    <p>Бүтээгдэхүүнээ сонгоод үндсэн мэдээлэл, цагийн хуваарь, үнэ, зураг удирдах.</p>
                </TitleBlock>
                <HeaderMeta>
                    <div className="pill">{selectedMajorGroupLabel || "Ангилал сонгоогүй"}</div>
                    <div className="pill secondary">{products.length} бүтээгдэхүүн</div>
                    {selectedMajorGroup && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleCreateProduct}
                        />
                    )}
                </HeaderMeta>
            </HeaderRow>

            <SplitLayout>
                <LeftPanel>
                    <SectionTitle>Бүтээгдэхүүн</SectionTitle>
                    <Select
                        placeholder="Ангилал сонгоно уу"
                        value={selectedMajorGroup}
                        onChange={setSelectedMajorGroup}
                        options={majorGroupOptions}
                    />
                    <Input
                        placeholder="Нэрээр хайх"
                        prefix={<SearchOutlined />}
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                    />
                    {selectedMajorGroup && (
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Tag-аар шүүх"
                            value={selectedTags}
                            onChange={setSelectedTags}
                            options={tagOptions}
                        />
                    )}
                    <Divider />
                    <ProductList
                        dataSource={filteredProducts}
                        locale={{ emptyText: "Бараа олдсонгүй" }}
                        renderItem={(item) => {
                            const tagBadges = getTagBadges(item.tags);
                            return (
                                <ProductRow
                                    $active={item.id === selectedId}
                                    onClick={() => setSelectedId(item.id)}
                                >
                                    <div className="info">
                                        <strong>{item.name}</strong>
                                        {tagBadges.length > 0 && (
                                            <div className="tags">
                                                {tagBadges.map((tagBadge) => (
                                                    <span
                                                        className="tag"
                                                        key={`${item.id || item.name}_${tagBadge.label}`}
                                                        style={getTagBadgeStyle(tagBadge.color)}
                                                    >
                                                        {tagBadge.label}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="meta">
                                        {item.type}
                                    </div>
                                </ProductRow>
                            );
                        }}
                    />
                </LeftPanel>

                <RightPanel>
                    {!selectedProduct ? (
                        <EmptyState>
                            <div className="title">Бүтээгдэхүүн сонгоно уу</div>
                            <p>
                                Эхлээд ангилал сонгоод, жагсаалтаас бараагаа сонгоно.
                            </p>
                        </EmptyState>
                    ) : (
                        <>
                            <CardHeader>
                                <div>
                                    <div className="card_header_title">
                                        <h2>{selectedProduct?.name}</h2>
                                        <div className="header_actions">
                                            <Button
                                                onClick={handleDuplicateProduct}
                                                loading={isDuplicating}
                                                disabled={selectedProduct?.isDraft}
                                            >
                                                <CopyOutlined />
                                            </Button>
                                            <Button
                                                danger
                                                onClick={handleDeleteProduct}
                                                loading={isDeleting}
                                                disabled={selectedProduct?.isDraft}
                                            >
                                                <DeleteOutlined />
                                            </Button>
                                        </div>
                                    </div>
                                    <span>{selectedProductGroupLabel}</span>

                                </div>
                            </CardHeader>

                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                items={[
                                    {
                                        key: "details",
                                        label: "Үндсэн мэдээлэл",
                                        children: (
                                            <DetailsTab
                                                instance={selectedProduct}
                                                menuId={selectedProduct?.isDraft ? null : selectedId}
                                                form={form}
                                                categoryOptions={majorGroupOptions}
                                                statusValue={statusValue}
                                                isVisibleValue={isVisibleValue}
                                                onStatusChange={setStatusValue}
                                                onIsVisibleChange={setIsVisibleValue}
                                                onTagAssigned={handleTagAssigned}
                                                onTagUnassigned={handleTagUnassigned}
                                            />
                                        ),
                                    },
                                    {
                                        key: "customization",
                                        label: "Item customization",
                                        children: (
                                            <ItemCustomizationTab
                                                menu={selectedProduct}
                                                modifiers={modifiers}
                                                definitions={selectedProduct?.definitions || []}
                                                onDefinitionChange={handleDefinitionChange}
                                                onDeleteDefinition={handleDefinitionDelete}
                                                onAddDefinition={handleAddDefinition}
                                            />
                                        ),
                                    },
                                    {
                                        key: "availability",
                                        label: "Цагийн хуваарь",
                                        children: (
                                            <AvailabilityTab
                                                menuId={selectedId}
                                            />
                                        ),
                                    },
                                    {
                                        key: "images",
                                        label: "Зурагнууд",
                                        children: (
                                            <ImagesTab
                                                image={imageSlots}
                                                availableImages={availableImages}
                                                onImageSelect={handleImageSelect}
                                                menuId={selectedProduct?.isDraft ? null : selectedId}
                                                instance={selectedProduct}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </>
                    )}
                </RightPanel>
            </SplitLayout>
        </PageWrap>
    );
};

const PageWrap = styled.div`
  padding: 28px;
  min-height: 100vh;
  background: radial-gradient(circle at top, #fdf2f8 0%, #f5f7fb 38%, #eef2ff 100%);
  color: ${mainColors.textPrimary};
  font-family: "Sora", "SF Pro Text", "Segoe UI", sans-serif;
  --radius-lg: 8px;
  --radius-md: 6px;
  --radius-sm: 5px;
  --shadow-soft: 0 18px 40px rgba(15, 23, 42, 0.08);
  --shadow-card: 0 14px 30px rgba(15, 23, 42, 0.08);

  .ant-btn,
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-picker,
  .ant-card,
  .ant-radio-button-wrapper,
  .ant-tag,
  .ant-upload-select,
  .ant-modal-content,
  .ant-modal-header,
  .ant-modal-footer {
    border-radius: var(--radius-sm) !important;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector {
    border-color: ${mainColors.border} !important;
  }

  .ant-tabs-tab {
    font-weight: 600;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
  padding: 18px 22px;
  border-radius: 16px;
  border: 1px solid rgba(22, 119, 255, 0.18);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 18px 50px rgba(33, 49, 63, 0.1);
  backdrop-filter: blur(8px);

  h1 {
    margin: 0 0 6px 0;
    font-size: 22px;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0;
    color: #6b7a88;
    max-width: 560px;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderMeta = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  .pill {
    align-content: center;
    background: ${mainColors.surface};
    border: 1px solid ${mainColors.border};
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    color: ${mainColors.textPrimary};
    box-shadow: 0 8px 16px rgba(15, 23, 42, 0.06);
  }

  .pill.secondary {
    background: ${mainColors.accentSoft};
    color: ${mainColors.accent};
  }
`;

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled(Card)`
  height: 100%;
  max-height: calc( 100vh - 130px);
  min-height: 520px;
  border-radius: var(--radius-lg);
  background: ${mainColors.surface};
  border: 1px solid ${mainColors.border};
  box-shadow: var(--shadow-card);
  overflow: hidden;

  .ant-card-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 100%;
    overflow: hidden;
  }
  .ant-divider {
    margin: 8px 0;
  }
`;

const RightPanel = styled(Card)`
  border-radius: var(--radius-lg);
  border: 1px solid ${mainColors.border};
  box-shadow: var(--shadow-soft);
  background: ${mainColors.surface};

  .ant-card-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  > div {
    width: 100%;
    min-width: 0;
  }

  .card_header_title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;

    h2 {
      margin: 0;
      font-size: 20px;
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .header_actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  span {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: ${mainColors.textSecondary};
  }

  @media (max-width: 640px) {
    .card_header_title h2 {
      font-size: 18px;
    }
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
`;

const ProductList = styled(List)`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding-right: 4px;
`;

const ProductRow = styled(List.Item)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px !important;
  border-radius: var(--radius-sm);
  cursor: pointer;
  margin: 4px;
  border: 1px solid
    ${({ $active }) => ($active ? mainColors.accent : mainColors.border)} !important;
  border-block-end: 1px solid
    ${({ $active }) => ($active ? mainColors.accent : mainColors.border)} !important;
  background: ${({ $active }) => ($active ? "#eef2ff" : "#ffffff")};
  transition: all 0.2s ease;
  &:hover {
    border-color: ${mainColors.accent};
    transform: translateY(-1px);
  }

  .info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  strong {
    font-weight: 600;
    line-height: 1.25;
    word-break: break-word;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .tag {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid rgba(37, 99, 235, 0.24);
    background: rgba(37, 99, 235, 0.08);
    color: ${mainColors.textSecondary};
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta {
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 12px;
    color: ${mainColors.textSecondary};
  }
`;

const EmptyState = styled.div`
  border: 1px dashed ${mainColors.border};
  border-radius: var(--radius-lg);
  padding: 36px;
  text-align: center;
  background: ${mainColors.surfaceAlt};
  color: ${mainColors.textSecondary};
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .title {
    font-size: 18px;
    font-weight: 600;
    color: ${mainColors.textPrimary};
    margin-bottom: 8px;
  }
`;

export default Products;
